import { useEffect, useState } from 'react';
import { api, getToken, setToken, clearToken } from './api';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Settings from './pages/Settings';
import Usage from './pages/Usage';

type Tab = 'dashboard' | 'accounts' | 'settings' | 'usage';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'dashboard', label: '仪表盘', icon: '📊' },
  { id: 'accounts', label: '账户管理', icon: '👥' },
  { id: 'settings', label: '统一 API', icon: '🔑' },
  { id: 'usage', label: '使用记录', icon: '📈' },
];

export default function App() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [loginError, setLoginError] = useState('');
  const [busy, setBusy] = useState(false);
  const [user, setUser] = useState('');

  useEffect(() => {
    if (getToken()) {
      api.stats().catch(() => clearToken());
    }
  }, []);

  const doLogin = async (username: string, password: string) => {
    setBusy(true);
    setLoginError('');
    try {
      const r = await api.login(username, password);
      setToken(r.token);
      setUser(r.username);
    } catch (e) {
      setLoginError(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  const doLogout = () => { clearToken(); setUser(''); };

  if (!getToken()) {
    return <LoginPage onLogin={doLogin} busy={busy} error={loginError} />;
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-12 shrink-0 items-center border-b border-[#edeff2] bg-[#f8fafc] px-4">
        <span className="text-lg">🧭</span>
        <span className="ml-2 text-sm font-bold text-[#272b33]">AgentDesk 主后台</span>
        <span className="ml-2 rounded bg-[#f0f2f7] px-1.5 py-0.5 text-[10px] text-[#565a60]">{user || 'admin'}</span>
        <nav className="ml-8 flex flex-1 gap-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={'rounded-md px-3 py-1.5 text-[13px] ' + (tab === t.id ? 'bg-[#f0f2f7] text-white' : 'text-[#565a60] hover:bg-[#f0f2f7]/50')}>
              <span className="mr-1">{t.icon}</span>{t.label}
            </button>
          ))}
        </nav>
        <button onClick={doLogout} className="rounded-md px-2 py-1 text-xs text-[#898b8f] hover:bg-[#f0f2f7] hover:text-[#565a60]">退出</button>
      </header>
      <main className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'dashboard' && <Dashboard />}
        {tab === 'accounts' && <Accounts />}
        {tab === 'settings' && <Settings />}
        {tab === 'usage' && <Usage />}
      </main>
    </div>
  );
}

function LoginPage({ onLogin, busy, error }: { onLogin: (u: string, p: string) => void; busy: boolean; error: string }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  return (
    <div className="flex h-full items-center justify-center">
      <div className="w-80 rounded-xl border border-[#edeff2] bg-white p-6">
        <div className="mb-5 text-center">
          <div className="text-3xl">🧭</div>
          <h1 className="mt-2 text-lg font-bold text-[#272b33]">AgentDesk 主后台</h1>
          <p className="mt-1 text-xs text-[#898b8f]">统一账户与 API 管理</p>
        </div>
        <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="用户名"
          className="mb-2 w-full rounded-md border border-[#e1e3e5] bg-white px-3 py-2 text-sm text-[#272b33] placeholder-[#bcbdc0] focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
        <input value={password} type="password" onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onLogin(username, password)}
          placeholder="密码"
          className="mb-3 w-full rounded-md border border-[#e1e3e5] bg-white px-3 py-2 text-sm text-[#272b33] placeholder-[#bcbdc0] focus:outline-none focus:ring-2 focus:ring-indigo-500/40" />
        {error && <div className="mb-2 text-xs text-[#fa2331]">{error}</div>}
        <button onClick={() => onLogin(username, password)} disabled={busy || !username || !password}
          className="w-full rounded-md bg-[#1966ff] py-2 text-sm font-medium text-white hover:bg-[#3377ff] disabled:opacity-40">
          {busy ? '登录中…' : '登录'}
        </button>
      </div>
    </div>
  );
}